import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import NavBar from '../components/Navbar/NavBar';
import Footer from '../components/Footer';
import { useDocTitle } from '../hooks/CustomHook';

const principles = [
    {
        title: '隐私安全',
        text: '家庭数据自主掌控，让记录只属于家人。',
    },
    {
        title: '技术坚守',
        text: '珍贵回忆，不因时间流逝而遗失。',
    },
    {
        title: '温暖连接',
        text: '技术服务于亲情，让每一份爱都能跨越时间传递。',
    },
    {
        title: '持续成长',
        text: '倾听用户需求，持续优化体验与功能。',
    },
];

const useInView = (options) => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setVisible(true);
                observer.disconnect();
            }
        }, options);
        observer.observe(el);
        return () => observer.disconnect();
    }, [options]);

    return [ref, visible];
};

const Reveal = ({ children, className = '', delay = 0 }) => {
    const [ref, visible] = useInView({ threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    return (
        <div
            ref={ref}
            className={className}
            style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(28px)',
                transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
            }}
        >
            {children}
        </div>
    );
};

const About = () => {
    useDocTitle('江西族迹 | 关于我们');

    return (
        <>
            <NavBar />
            <main className="about-page bg-[#f4f7fb] text-gray-800">
                {/* Hero：品牌优先，单一构图 */}
                <header className="relative overflow-hidden pt-28 pb-16 md:pt-36 md:pb-24">
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                'radial-gradient(ellipse 80% 60% at 20% 0%, #dbeafe 0%, transparent 55%), radial-gradient(ellipse 70% 50% at 90% 20%, #e0f2fe 0%, transparent 50%), linear-gradient(180deg, #eff6ff 0%, #f4f7fb 100%)',
                        }}
                    />
                    <div className="relative max-w-3xl mx-auto px-6 text-center">
                        <p className="text-sm tracking-[0.28em] text-blue-700/80 font-semibold mb-5 animate-[aboutFade_0.8s_ease_both]">
                            江西族迹
                        </p>
                        <h1 className="text-4xl md:text-5xl lg:text-[3.25rem] font-bold leading-tight text-slate-900 mb-5 animate-[aboutFade_0.9s_ease_0.08s_both]">
                            关于我们
                        </h1>
                        <p className="text-xl md:text-2xl text-blue-800 font-medium leading-snug mb-6 animate-[aboutFade_0.9s_ease_0.16s_both]">
                            存续家族记忆，传承血脉亲情
                        </p>
                        <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto animate-[aboutFade_0.9s_ease_0.24s_both]">
                            我们致力于让数字技术连接亲情，让成长、人生与家庭故事得以长期保存，让每一份珍贵回忆都有迹可循，让家的故事在数字时代持续延续。
                        </p>
                    </div>
                </header>

                <div className="max-w-3xl mx-auto px-6 pb-20 md:pb-28 space-y-20 md:space-y-28">
                    <Reveal>
                        <section>
                            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-5">
                                我们为什么而存在
                            </h2>
                            <div className="h-1 w-14 bg-blue-600 rounded-full mb-8" />
                            <p className="text-lg text-slate-600 leading-[1.85]">
                                数字时代，我们记录了越来越多，却未必留下了更多。孩子成长的点滴散落在手机相册，父母的人生故事停留在记忆里，家人的珍贵瞬间随着时间被遗忘。我们希望打造一个真正属于家庭的数字空间，把成长、亲情与人生重新连接，让记忆不再零散，让故事得以延续。
                            </p>
                        </section>
                    </Reveal>

                    <Reveal delay={60}>
                        <section>
                            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-5">
                                我们在做什么
                            </h2>
                            <div className="h-1 w-14 bg-blue-600 rounded-full mb-8" />
                            <p className="text-lg text-slate-600 leading-[1.85]">
                                族语不仅是一款应用，更是一套长期守护家庭记忆的数字服务。记录孩子成长、整理长辈人生、连接家庭成员、沉淀家族故事，让每一次记录都成为未来回望时的一份陪伴，让每一个家庭都能共同建设属于自己的数字记忆馆。
                            </p>
                        </section>
                    </Reveal>

                    <Reveal delay={60}>
                        <section>
                            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-5">
                                我们坚持
                            </h2>
                            <div className="h-1 w-14 bg-blue-600 rounded-full mb-10" />
                            <ul className="divide-y divide-slate-200/80 border-y border-slate-200/80">
                                {principles.map((item) => (
                                    <li key={item.title} className="py-6 md:py-7 flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-8">
                                        <span className="sm:w-28 flex-shrink-0 text-blue-700 font-semibold text-lg">
                                            {item.title}
                                        </span>
                                        <span className="text-slate-600 text-base md:text-lg leading-relaxed">
                                            {item.text}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    </Reveal>

                    <Reveal delay={60}>
                        <section>
                            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-5">
                                我们的愿景
                            </h2>
                            <div className="h-1 w-14 bg-blue-600 rounded-full mb-8" />
                            <div className="space-y-5 text-lg text-slate-600 leading-[1.85]">
                                <p>我们相信，每一个家庭都值得拥有一份不会消失的记忆。</p>
                                <p>
                                    未来，当孩子长大、父母老去、家庭不断延续，每一代人都能透过这些记录，看见家人的成长、了解家族的故事、感受亲情的温度。
                                </p>
                                <p className="text-slate-800 font-medium">
                                    守护家庭记忆，延续血脉亲情，让家的故事世代相传。
                                </p>
                            </div>
                            <div className="mt-10 flex flex-col sm:flex-row gap-4">
                                <Link
                                    to="/app"
                                    className="inline-flex items-center justify-center px-8 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors duration-300"
                                >
                                    了解族语APP
                                </Link>
                                <Link
                                    to="/download"
                                    className="inline-flex items-center justify-center px-8 py-3.5 rounded-xl border-2 border-blue-600 text-blue-700 hover:bg-blue-50 font-medium transition-colors duration-300"
                                >
                                    下载族语
                                </Link>
                            </div>
                        </section>
                    </Reveal>
                </div>
            </main>
            <Footer />
            <style>{`
                @keyframes aboutFade {
                    from { opacity: 0; transform: translateY(16px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </>
    );
};

export default About;
